import React from 'react';
import {useTranslation} from 'react-i18next';
import {PackageOpen} from 'lucide-react';
import {cn} from '@/shared/utils/utils.ts';
import {Button} from './Button';
import {motion} from 'framer-motion';

interface EmptyStateProps {
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
  icon?: React.ElementType;
  image?: string;
}

/**
 * EmptyState Component
 *
 * Standardized empty state display for lists, dashboards, and search results.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  actionLabel,
  onAction,
  className,
  icon: Icon = PackageOpen,
  image,
}) => {
  const { t } = useTranslation();
  const defaultTitle = title ?? t('ui.emptystate.no_items_found');

  return (
    <motion.div
      className={cn(
        'flex flex-col items-center justify-center text-center p-8 sm:p-12 animate-fade-in',
        className
      )}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {image ? (
        <img
          src={image}
          alt=""
          className="mb-6 h-40 w-auto object-contain opacity-80"
        />
      ) : (
        <div className="mb-6 rounded-full bg-muted/30 p-4">
          {React.createElement(Icon, { className: "h-10 w-10 text-muted-foreground opacity-60" })}
        </div>
      )}

      <h3 className="mb-2 text-body-xl font-semibold text-foreground">
        {defaultTitle}
      </h3>

      {description && (
        <p className="mb-8 max-w-sm text-muted-foreground text-pretty">
          {description}
        </p>
      )}

      {actionLabel && onAction && (
        <Button
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      )}
    </motion.div>
  );
};
